<!-- Zijbalk-inhoud (groepen, samenstelling, snelle acties) is gedeeld tussen de
     desktop-/tablet-zijbalk en het inklapbare phone-paneel: per toestand wordt
     precies één van beide outlets geïnstantieerd (docs/MOBIEL.md §6). -->
<ng-template #sidebarContent>
  <div class="chart-manager-sidebar-header">
    <span class="chart-manager-sidebar-title">{{ text().groupsTitle }}</span>
    <button
      eBtn
      dense
      size="sm"
      icon="plus"
      iconColor="primary-75"
      [tooltip]="text().newGroup"
      (click)="openCreateGroup()"
    ></button>
  </div>
  @if (groupError()) {
    <div class="chart-manager-sidebar-error">{{ groupError() }}</div>
  }
  <hd-chart-group-tree
    [groups]="groups()"
    [selectedGroupId]="selectedGroupId()"
    [dropZoneHint]="chartDragActive()"
    [flashGroupId]="flashGroupId()"
    (selectedGroupIdChange)="onGroupSelected($event)"
    (editRequested)="openEditGroup($event)"
    (deleteRequested)="deleteGroup($event)"
    (moveRequested)="onGroupMoved($event)"
    (chartDropped)="onChartDroppedOnGroup($event)"
    (reorderRequested)="onGroupsReordered($event)"
  ></hd-chart-group-tree>

  @if (composition(); as compositionVm) {
    <div class="chart-manager-sidebar-header chart-manager-quick-actions-header">
      <span class="chart-manager-sidebar-title">{{ text().compositionTitle }} ({{ compositionVm.total }})</span>
      <button
        type="button"
        class="chart-composition-toggle"
        (click)="compositionOpen.set(!compositionOpen())"
      >
        <i [class]="'fas fa-chevron-' + (compositionOpen() ? 'up' : 'down')" aria-hidden="true"></i>
      </button>
    </div>
    @if (compositionOpen()) {
      <div class="chart-composition">
        @for (type of compositionVm.types; track type.label) {
          <div class="chart-composition-row" [title]="type.label + ' (' + type.count + ')'">
            <span class="chart-composition-icon">
              @if (type.icon) {
                <i [class]="'fas fa-' + type.icon" aria-hidden="true"></i>
              }
            </span>
            <span class="chart-composition-bar-track">
              <span class="chart-composition-bar" [style.width.%]="type.pct"></span>
            </span>
            <span class="chart-composition-count">{{ type.count }}</span>
          </div>
        }
        @if (compositionVm.authorities) {
          <div class="chart-composition-line">{{ compositionVm.authorities }}</div>
        }
        @if (compositionVm.profiles) {
          <div class="chart-composition-line">{{ compositionVm.profiles }}</div>
        }
      </div>
    }
  }

  <!-- Snelle acties staan op desktop in de header (icoonknoppen); op phone
       verschijnen ze via #phoneQuickActions in het filters-paneel. Zo houdt de
       zijbalk maximale ruimte voor de groepenboom. -->
</ng-template>

<!-- Phone: snelle acties als volle-breedte-knoppen in het inklapbare paneel. -->
<ng-template #phoneQuickActions>
  <div class="chart-manager-sidebar-header chart-manager-quick-actions-header">
    <span class="chart-manager-sidebar-title">{{ text().quickActionsTitle }}</span>
  </div>
  <button
    eBtn
    color="highlight"
    icon="star"
    iconColor="primary-75"
    class="chart-manager-quick-action"
    (click)="loadMyChartAction()"
  >{{ text().loadMyChart }}</button>
  <button
    eBtn
    color="highlight"
    icon="cake-candles"
    iconColor="primary-75"
    class="chart-manager-quick-action"
    (click)="birthdaysDialogOpen.set(true)"
  >{{ text().birthdaysAction }}</button>
  <button
    eBtn
    color="highlight"
    icon="trash-can"
    iconColor="primary-75"
    class="chart-manager-quick-action"
    (click)="trashDialogOpen.set(true)"
  >{{ text().trashAction }}</button>
  <!-- Export hier i.p.v. een extra knop in de phone-kopbalk (docs/MOBIEL.md). -->
  <button
    eBtn
    color="highlight"
    icon="file-export"
    iconColor="primary-75"
    class="chart-manager-quick-action"
    (click)="openExport('list')"
  >{{ text().exportAction }}</button>
  @if (shareAccess.featureGranted()) {
    <button
      eBtn
      color="highlight"
      icon="share-nodes"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      (click)="sharedDialogOpen.set(true)"
      data-test-id="manager-shared-charts-action"
    >{{ text().sharedChartsAction }}</button>
  }
  @if (mode() === 'dialog') {
    <!-- Enkel in de chart-omgeving (dialog-modus): de pagina opent er de
         spelkeuze-dialog mee, dezelfde als via de spelmodus-shortcut. -->
    <button
      eBtn
      color="highlight"
      icon="gamepad"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      data-test-id="manager-game-action"
      (click)="gameRequested.emit()"
    >{{ text().gameAction }}</button>
    <!-- Het huidige moment als eigen tab: volledig (design + personality) of
         enkel de transit (personality-only = het echte Just Now), en een verse
         lege tab — alle drie sluiten de manager. -->
    <button
      eBtn
      color="highlight"
      [icon]="justNowIcons.full"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      data-test-id="manager-just-now-full-action"
      [tooltip]="text().justNowFullTooltip"
      (click)="justNowFullRequested.emit()"
    >{{ text().justNowFullAction }}</button>
    <button
      eBtn
      color="highlight"
      [icon]="justNowIcons.transit"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      data-test-id="manager-just-now-action"
      [tooltip]="text().justNowTooltip"
      (click)="justNowRequested.emit()"
    >{{ text().justNowAction }}</button>
    <!-- Rave New Year: jaarkiezer met dezelfde twee varianten als Just Now. -->
    <button
      eBtn
      color="highlight"
      [icon]="raveNewYearIcon"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      data-test-id="manager-rave-new-year-action"
      [tooltip]="text().raveNewYearTooltip"
      (click)="raveNewYearDialogOpen.set(true)"
    >{{ text().raveNewYearAction }}</button>
    <button
      eBtn
      color="highlight"
      icon="file"
      iconColor="primary-75"
      class="chart-manager-quick-action"
      data-test-id="manager-empty-chart-action"
      (click)="emptyChartRequested.emit()"
    >{{ text().emptyChartAction }}</button>
  }
  @if (quickActionMessage()) {
    <div class="chart-manager-sidebar-error">{{ quickActionMessage() }}</div>
  }
</ng-template>

<!-- Eigenschappen-filterbalk: gedeeld tussen de tabel-header (desktop/tablet)
     en het phone-paneel. -->
<ng-template #propertyFilterRow>
  <div class="chart-manager-header-row">
    <hd-chart-property-filter
      [applyHint]="text().filterApplyHint"
      [label]="text().propertyType"
      [options]="propertyFilterOptions().types"
      [selected]="propertyFilters().hdTypes ?? []"
      minWidth="240px"
      (selectedChange)="onPropertyFilterChange('hdTypes', $event)"
    ></hd-chart-property-filter>
    <hd-chart-property-filter
      [applyHint]="text().filterApplyHint"
      [label]="text().propertyAuthority"
      [options]="propertyFilterOptions().authorities"
      [selected]="propertyFilters().hdAuthorities ?? []"
      minWidth="200px"
      (selectedChange)="onPropertyFilterChange('hdAuthorities', $event)"
    ></hd-chart-property-filter>
    <hd-chart-property-filter
      [applyHint]="text().filterApplyHint"
      [label]="text().propertyDefinition"
      [options]="propertyFilterOptions().definitions"
      [selected]="propertyFilters().hdDefinitions ?? []"
      minWidth="160px"
      (selectedChange)="onPropertyFilterChange('hdDefinitions', $event)"
    ></hd-chart-property-filter>
    <hd-chart-property-filter
      [applyHint]="text().filterApplyHint"
      [label]="text().propertyProfile"
      [options]="propertyFilterOptions().profiles"
      [selected]="propertyFilters().hdProfiles ?? []"
      minWidth="160px"
      (selectedChange)="onPropertyFilterChange('hdProfiles', $event)"
    ></hd-chart-property-filter>
    <!-- Poortfilter: een nummer typen treft exact die poort; vanaf twee poorten
         kiest de En/Of-toggle of een chart ze allemaal of minstens één activeert. -->
    <hd-chart-property-filter
      [applyHint]="text().filterApplyHint"
      [label]="text().gateFilter"
      [options]="propertyFilterOptions().gates"
      [selected]="gateFilterSelected()"
      [searchFn]="gateFilterSearch"
      minWidth="200px"
      (selectedChange)="onGateFilterChange($event)"
    ></hd-chart-property-filter>
    @if (gateFilter().gates.length > 1) {
      <e-segmented-toggle
        [options]="gateMatchOptions()"
        [value]="gateFilter().match"
        [groupLabel]="text().gateFilter"
        testId="chart-manager-gate-match"
        (valueChange)="onGateMatchChange($event)"
      ></e-segmented-toggle>
    }
    <hd-chart-center-filter
      [selected]="propertyFilters().centerStates ?? {}"
      (selectedChange)="onCenterFilterChange($event)"
    ></hd-chart-center-filter>
    <button
      eBtn
      color="highlight"
      icon="filter-circle-xmark"
      iconColor="primary-75"
      [tooltip]="text().clearFilters"
      (click)="clearPropertyFilters()"
    ></button>
  </div>
</ng-template>

<div class="chart-manager-layout">
  @if (!isPhone()) {
    <aside class="chart-manager-sidebar">
      <ng-container [ngTemplateOutlet]="sidebarContent"></ng-container>
    </aside>
  }

  <div class="chart-manager-content">
    @if (isPhone()) {
      <!-- Phone: kaartenlijst i.p.v. de tabel. Zoeken op volle breedte; groepen
           + filters achter een toggle; bulk/backfill/drag bestaan hier niet. -->
      <div class="chart-manager-phone-header">
        <e-search-input
          ngDefaultControl
          name="search"
          withButton
          border
          [placeholder]="searchPlaceholder()"
          [ngModel]="searchModel"
          (ngModelChange)="onSearchModelChange($event)"
          (searchClick)="onSearch(searchModel)"
        ></e-search-input>
        <div class="chart-manager-phone-actions">
          <button
            eBtn
            color="highlight"
            icon="sliders"
            iconColor="primary-75"
            class="chart-manager-phone-toggle"
            (click)="phonePanelOpen.set(!phonePanelOpen())"
          >{{ text().phoneFiltersToggle }}</button>
          <e-flex-spacer></e-flex-spacer>
          <button
            eBtn
            color="highlight"
            icon="file-import"
            iconColor="primary-75"
            [tooltip]="text().importAction"
            (click)="importDialogOpen.set(true)"
          ></button>
          <button eBtn color="primary-75" icon="plus" (click)="openCreate()">{{ text().newChart }}</button>
        </div>
      </div>

      @if (phonePanelOpen()) {
        <div class="chart-manager-phone-panel">
          <ng-container [ngTemplateOutlet]="propertyFilterRow"></ng-container>
          <ng-container [ngTemplateOutlet]="sidebarContent"></ng-container>
          <ng-container [ngTemplateOutlet]="phoneQuickActions"></ng-container>
        </div>
      }

      <div class="chart-card-list" contentLoader [loading]="loading()">
        @if (!loading() && items().length === 0) {
          <div class="chart-card-empty">{{ text().phoneEmpty }}</div>
        }
        @for (item of items(); track item.id) {
          <div class="chart-card">
            <!-- Tap op de kaart = dezelfde actie als de laad-knop in de tabel. -->
            <button type="button" class="chart-card-main" (click)="loadChart(item.id)">
              <span class="chart-card-name">
                {{ item.name }}
                @if (isBirthdaySoon(item)) {
                  <i class="fas fa-cake-candles chart-birthday-icon" [title]="birthdayTooltip(item)" aria-hidden="true"></i>
                }
                @if (item.isShared && shareAccess.featureGranted()) {
                  <i class="fas fa-share-nodes chart-shared-icon" [title]="text().sharedTooltip" aria-hidden="true" data-test-id="manager-shared-icon"></i>
                }
              </span>
              <span class="chart-card-line">
                @if (energyTypeIcon(item); as icon) {
                  <i eIcon [icon]="icon"></i>
                }
                {{ energyTypeLabel(item) }}
                @if (item.hdProfile) {
                  <span class="chart-card-sep">·</span>{{ item.hdProfile }}
                }
              </span>
              <span class="chart-card-line chart-card-line-muted">
                {{ formatBirthDate(item) }}
                @if (item.birthCity) {
                  <span class="chart-card-sep">·</span>{{ item.birthCity }}
                }
              </span>
            </button>
            <e-dropdown-menu #cardMenu position="left" class="chart-card-menu">
              <ng-template eSlot="activator">
                <button
                  type="button"
                  class="chart-card-menu-activator"
                  [attr.aria-label]="text().phoneActions"
                  (click)="cardMenu.toggleMenu()"
                >
                  <i eIcon icon="ellipsis-v"></i>
                </button>
              </ng-template>
              <ng-template eSlot="menu">
                <e-dropdown-menu-item icon="pen" (click)="openEdit(item); cardMenu.toggleMenu(false)">
                  {{ text().edit }}
                </e-dropdown-menu-item>
                <e-dropdown-menu-item icon="folder-tree" (click)="openSetGroups(item); cardMenu.toggleMenu(false)">
                  {{ text().setGroups }}
                </e-dropdown-menu-item>
                <e-dropdown-menu-item icon="link" (click)="openRelations(item); cardMenu.toggleMenu(false)">
                  {{ text().relationsTitle }}
                </e-dropdown-menu-item>
                @if (shareAccess.featureGranted()) {
                  <e-dropdown-menu-item icon="share-nodes" (click)="openShare(item); cardMenu.toggleMenu(false)">
                    {{ text().share }}
                  </e-dropdown-menu-item>
                }
                <e-dropdown-menu-item icon="trash" iconColor="danger" (click)="deleteChart(item); cardMenu.toggleMenu(false)">
                  {{ text().delete }}
                </e-dropdown-menu-item>
              </ng-template>
            </e-dropdown-menu>
          </div>
        }
      </div>

      @if (totalRecordCount() > pageSize()) {
        <div class="chart-card-pager">
          <e-paginator
            [totalCount]="totalRecordCount()"
            [pageSize]="pageSize()"
            [currentPage]="page()"
            (currentPageChange)="onPageChange($event)"
          ></e-paginator>
        </div>
      }
    } @else {
    <e-advanced-table
  [headers]="headers()"
  [items]="items()"
  identifier="id"
  [loading]="loading()"
  paginate
  [currentPage]="page()"
  [pageSize]="pageSize()"
  [pageSizeOptions]="pageSizeOptions"
  [totalCount]="totalRecordCount()"
  [hideColumns]="hiddenColumns()"
  (currentPageChange)="onPageChange($event)"
  (pageSizeChange)="onPageSizeChange($event)"
  (sort)="onSort($event)"
  (filter)="onFilter($event)"
  (rowExpandedChange)="onRowExpandedChange($event)"
  rowsDraggable
  (rowDragStart)="onChartRowDragStart($event)"
  (rowDragEnd)="onChartRowDragEnd()"
  selectable
  (selectionChange)="onSelectionChange($event)"
>
  <div slot="prepend-header" class="chart-manager-header">
    <div class="chart-manager-header-row">
      <e-search-input
        ngDefaultControl
        name="search"
        withButton
        border
        autoFocus
        [placeholder]="searchPlaceholder()"
        [ngModel]="searchModel"
        (ngModelChange)="onSearchModelChange($event)"
        (searchClick)="onSearch(searchModel)"
      ></e-search-input>
      <e-flex-spacer></e-flex-spacer>
      @if (quickActionMessage()) {
        <span class="backfill-status">{{ quickActionMessage() }}</span>
      }
      <!-- Snelle acties (desktop): compacte icoonknoppen; labels zitten in de tooltips.
           Zo blijft de zijbalk volledig voor de groepenboom. -->
      <button
        eBtn
        color="highlight"
        icon="star"
        iconColor="primary-75"
        [tooltip]="text().loadMyChart"
        (click)="loadMyChartAction()"
      ></button>
      <button
        eBtn
        color="highlight"
        icon="cake-candles"
        iconColor="primary-75"
        [tooltip]="text().birthdaysAction"
        (click)="birthdaysDialogOpen.set(true)"
      ></button>
      <button
        eBtn
        color="highlight"
        icon="trash-can"
        iconColor="primary-75"
        [tooltip]="text().trashAction"
        (click)="trashDialogOpen.set(true)"
      ></button>
      @if (shareAccess.featureGranted()) {
        <button
          eBtn
          color="highlight"
          icon="share-nodes"
          iconColor="primary-75"
          [tooltip]="text().sharedChartsAction"
          (click)="sharedDialogOpen.set(true)"
        ></button>
      }
      @if (mode() === 'dialog') {
        <button
          eBtn
          color="highlight"
          icon="gamepad"
          iconColor="primary-75"
          [tooltip]="text().gameAction"
          (click)="gameRequested.emit()"
        ></button>
        <button
          eBtn
          color="highlight"
          [icon]="justNowIcons.full"
          iconColor="primary-75"
          data-test-id="manager-header-just-now-full"
          [tooltip]="text().justNowFullTooltip"
          (click)="justNowFullRequested.emit()"
        ></button>
        <button
          eBtn
          color="highlight"
          [icon]="justNowIcons.transit"
          iconColor="primary-75"
          data-test-id="manager-header-just-now-transit"
          [tooltip]="text().justNowTooltip"
          (click)="justNowRequested.emit()"
        ></button>
        <button
          eBtn
          color="highlight"
          [icon]="raveNewYearIcon"
          iconColor="primary-75"
          data-test-id="manager-header-rave-new-year"
          [tooltip]="text().raveNewYearTooltip"
          (click)="raveNewYearDialogOpen.set(true)"
        ></button>
        <button
          eBtn
          color="highlight"
          icon="file"
          iconColor="primary-75"
          [tooltip]="text().emptyChartAction"
          (click)="emptyChartRequested.emit()"
        ></button>
      }
      <span class="chart-manager-header-divider"></span>
      @if (backfillStatus()) {
        <span class="backfill-status">{{ backfillStatus() }}</span>
      }
      <button
        eBtn
        color="highlight"
        icon="rotate"
        iconColor="primary-75"
        [disabled]="backfillRunning()"
        [tooltip]="text().backfillAction"
        (click)="runBackfill()"
      ></button>
      <button
        eBtn
        color="highlight"
        icon="file-import"
        iconColor="primary-75"
        [tooltip]="text().importAction"
        data-test-id="chart-import-open"
        (click)="importDialogOpen.set(true)"
      ></button>
      <button
        eBtn
        color="highlight"
        icon="file-export"
        iconColor="primary-75"
        [tooltip]="text().exportAction"
        data-test-id="chart-export-open"
        (click)="openExport('list')"
      ></button>
      <button eBtn color="primary-75" icon="plus" (click)="openCreate()">{{ text().newChart }}</button>
    </div>

    <ng-container [ngTemplateOutlet]="propertyFilterRow"></ng-container>

    @if (selectedChartIds().length > 0) {
      <div class="chart-batch-bar">
        <span class="chart-batch-count">{{ batchSelectedText() }}</span>
        <!-- Actiekeuze: "verplaatsen" (default, haalt de charts uit de actieve groep)
             bestaat enkel mét een groepsselectie; op "Alle charts" is er alleen toevoegen. -->
        <ng-select
          class="chart-batch-action"
          appendTo="body"
          [items]="batchGroupActionOptions()"
          bindValue="value"
          bindLabel="label"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="batchGroupAction()"
          (ngModelChange)="batchGroupActionChoice.set($event)"
        ></ng-select>
        <ng-select
          class="chart-batch-group"
          appendTo="body"
          [items]="batchGroupOptions()"
          bindValue="value"
          bindLabel="label"
          [clearable]="false"
          [placeholder]="text().batchGroupPlaceholder"
          [ngModel]="batchGroupId()"
          (ngModelChange)="batchGroupId.set($event)"
        ></ng-select>
        <button
          eBtn
          color="primary-75"
          [icon]="batchApplyIcon"
          [disabled]="!batchGroupId()"
          (click)="applyBatchGroupAction()"
        >{{ batchApplyText() }}</button>
        @if (batchRemoveFromGroupText()) {
          <button
            eBtn
            color="highlight"
            icon="folder-minus"
            iconColor="primary-75"
            (click)="removeSelectionFromGroup()"
          >{{ batchRemoveFromGroupText() }}</button>
        }
        <button
          eBtn
          color="highlight"
          icon="trash-can"
          iconColor="danger"
          (click)="deleteSelection()"
        >{{ text().batchMoveToTrash }}</button>
        <button
          eBtn
          color="highlight"
          icon="file-export"
          iconColor="primary-75"
          data-test-id="chart-export-selection"
          (click)="openExport('selection')"
        >{{ text().exportSelectionAction }}</button>
        <button eBtn color="highlight" (click)="clearSelection()">{{ text().batchClearSelection }}</button>
      </div>
    }
  </div>

  <ng-container [eTableColumn]="'name'">
    <ng-template eTableCell let-item="item">
      <!-- Naam = zelfde actie als de laad-knop: chart openen in de omgeving. -->
      <button type="button" class="chart-name" [title]="text().load" (click)="loadChart(item.id)">{{ item.name }}</button>
      @if (isBirthdaySoon(item)) {
        <i class="fas fa-cake-candles chart-birthday-icon" [title]="birthdayTooltip(item)" aria-hidden="true"></i>
      }
      <!-- Deelstatus zonder uitklappen (spec 2026-10-20-chart-share §4.2). -->
      @if (item.isShared && shareAccess.featureGranted()) {
        <i class="fas fa-share-nodes chart-shared-icon" [title]="text().sharedTooltip" aria-hidden="true" data-test-id="manager-shared-icon"></i>
      }
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'typeDescription'">
    <ng-template eTableCell let-item="item">
      <hd-chart-type-tag [type]="item.type"></hd-chart-type-tag>
    </ng-template>
    <ng-template eTableColumnFilter let-getFilterValue="getFilterValue" let-changeFilterValue="changeFilterValue">
      <e-enum-select
        translationKey="chartType"
        [enum]="PlatformChartType"
        [value]="getFilterValue()"
        (valueChange)="changeFilterValue($event)"
      >
        <ng-template let-label="label" let-value="value">
          <span class="chart-type-option"><i eIcon [icon]="typeIcon(value)"></i>{{ label }}</span>
        </ng-template>
      </e-enum-select>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'birthDateUtc'">
    <ng-template eTableCell let-item="item">
      <span class="chart-date">{{ formatBirthDate(item) }}</span>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'hdType'">
    <ng-template eTableCell let-item="item">
      <span class="chart-property">
        @if (energyTypeIcon(item); as icon) {
          <i eIcon [icon]="icon"></i>
        }
        {{ energyTypeLabel(item) }}
      </span>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'hdAuthority'">
    <ng-template eTableCell let-item="item">
      <span class="chart-property">{{ authorityLabel(item) }}</span>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'hdDefinition'">
    <ng-template eTableCell let-item="item">
      <span class="chart-property">{{ definitionLabel(item) }}</span>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'hdProfile'">
    <ng-template eTableCell let-item="item">
      <span class="chart-property">{{ item.hdProfile || '—' }}</span>
    </ng-template>
  </ng-container>

  <ng-container [eTableColumn]="'actions'">
    <ng-template eTableCell let-item="item">
      <div class="chart-actions">
        <button
          eBtn
          size="sm"
          color="primary-75"
          icon="arrow-right-to-bracket"
          [tooltip]="text().load"
          (click)="loadChart(item.id)"
        ></button>
        <button
          eBtn
          size="sm"
          color="highlight"
          icon="folder-tree"
          iconColor="primary-75"
          [tooltip]="text().setGroups"
          (click)="openSetGroups(item)"
        ></button>
        <button
          eBtn
          size="sm"
          color="highlight"
          icon="link"
          iconColor="primary-75"
          [tooltip]="text().relationsTitle"
          (click)="openRelations(item)"
        ></button>
        <button
          eBtn
          size="sm"
          color="highlight"
          icon="pen"
          iconColor="primary-75"
          [tooltip]="text().edit"
          (click)="openEdit(item)"
        ></button>
        <button
          eBtn
          size="sm"
          color="highlight"
          icon="trash"
          iconColor="danger"
          [tooltip]="text().delete"
          (click)="deleteChart(item)"
        ></button>
      </div>
    </ng-template>
  </ng-container>

  <ng-container eTableRow>
    <ng-template eTableRowCollapsePane let-item="item">
      <div class="chart-detail">
        <div class="chart-detail-actions">
          <button
            eBtn
            size="sm"
            color="highlight"
            icon="star"
            [iconColor]="item.isMyChart ? 'primary-75' : 'primary-25'"
            (click)="toggleMyChart(item)"
          >{{ item.isMyChart ? text().unmarkMyChart : text().markMyChart }}</button>
          <!-- Levenstijdlijn alleen voor standaard charts (spec levenstijdlijn §2.6). -->
          @if (item.type === PlatformChartType.Standard) {
            <button
              eBtn
              size="sm"
              color="highlight"
              icon="timeline"
              (click)="timelineRequested.emit(item.id)"
              data-test-id="manager-detail-timeline"
            >{{ text().timeline }}</button>
          }
          <!-- Delen via link (spec 2026-10-20-chart-share §4.2). -->
          @if (shareAccess.featureGranted()) {
            <button
              eBtn
              size="sm"
              color="highlight"
              icon="share-nodes"
              [iconColor]="item.isShared ? 'primary-75' : 'primary-25'"
              (click)="openShare(item)"
              data-test-id="manager-detail-share"
            >{{ text().share }}</button>
          }
        </div>
        <div class="chart-detail-columns">
        @if (details()[item.id]?.preset?.configuration; as miniConfiguration) {
          <button
            type="button"
            class="chart-detail-mini"
            [title]="text().load"
            (click)="loadChart(item.id)"
          >
            <hd-bodygraph-chart
              [configuration]="miniConfiguration"
              interactionMode="event-only"
              [contextMenuEnabled]="false"
            ></hd-bodygraph-chart>
          </button>
        }
        <div class="chart-detail-rows">
        <div class="chart-detail-row"><span class="chart-detail-label">{{ text().hdSummary }}</span><span>{{ hdSummary(item) }}</span></div>
        <div class="chart-detail-row"><span class="chart-detail-label">{{ text().created }}</span><span>{{ formatTimestamp(item.createdOn) }}</span></div>
        <div class="chart-detail-row"><span class="chart-detail-label">{{ text().modified }}</span><span>{{ formatTimestamp(item.lastModifiedOn) }}</span></div>
        <div class="chart-detail-row"><span class="chart-detail-label">{{ text().detailGroups }}</span><span>{{ groupNames(item) }}</span></div>
        <div class="chart-detail-row">
          <span class="chart-detail-label">{{ text().detailRelations }}</span>
          <span>
            @if (chartRelations(item)?.length) {
              <span class="chart-relation-chips">
                @for (relation of chartRelations(item); track relation.id) {
                  <button
                    type="button"
                    class="chart-relation-chip"
                    [title]="relationLabel(relation.relationType)"
                    (click)="loadChart(relation.otherChartId!)"
                  >
                    <i eIcon [icon]="relationIcon(relation.relationType)"></i>{{ relation.otherChartName }}
                  </button>
                }
              </span>
            } @else {
              <span>—</span>
            }
          </span>
        </div>
        @if (details()[item.id]; as detail) {
          <div class="chart-detail-row"><span class="chart-detail-label">{{ text().description }}</span><span>{{ detail.meta.description || '—' }}</span></div>
          <div class="chart-detail-row"><span class="chart-detail-label">{{ text().timezone }}</span><span>{{ detail.meta.timezone || '—' }}</span></div>
          <div class="chart-detail-row"><span class="chart-detail-label">{{ text().latLon }}</span><span>{{ detail.meta.latitude }}, {{ detail.meta.longitude }}</span></div>
          <div class="chart-detail-row"><span class="chart-detail-label">{{ text().source }}</span><span>{{ detail.meta.source || '—' }}</span></div>
        } @else {
          <div class="chart-detail-loading">{{ text().loading }}</div>
        }
        </div>
        </div>
      </div>
    </ng-template>
  </ng-container>
    </e-advanced-table>
    }
  </div>
</div>

<!-- Paginamodus heeft geen omgeving om een onopgeslagen chart te tonen → geen "enkel berekenen"-vinkje. -->
<hd-chart-create-dialog
  [(open)]="createDialogOpen"
  [calculateOnlyOffered]="mode() === 'dialog'"
  (calculated)="onChartCalculated($event)"
></hd-chart-create-dialog>
<hd-chart-edit-dialog
  [(target)]="editTarget"
  [isMyChart]="editTargetIsMyChart()"
  (saved)="onChartSaved($event)"
></hd-chart-edit-dialog>
<hd-chart-share-dialog [(target)]="shareTarget" (shareChanged)="onShareChanged($event)"></hd-chart-share-dialog>
<hd-chart-group-edit-dialog
  [(target)]="groupEditTarget"
  [groups]="groups()"
  (saved)="onGroupSaved($event)"
></hd-chart-group-edit-dialog>
<hd-chart-set-groups-dialog
  [(target)]="setGroupsTarget"
  [groups]="groups()"
  (saved)="onSetGroupsSaved($event)"
  (createGroupRequested)="onSetGroupsCreateGroupRequested($event)"
></hd-chart-set-groups-dialog>
<hd-chart-relations-dialog
  [(target)]="relationsTarget"
  (changed)="onRelationsChanged($event)"
></hd-chart-relations-dialog>
<hd-chart-trash-dialog [(open)]="trashDialogOpen" (restored)="onChartRestored()"></hd-chart-trash-dialog>
<hd-chart-shared-list-dialog [(open)]="sharedDialogOpen" (chartOpenRequested)="loadChart($event)" (shareChanged)="onShareChanged($event)"></hd-chart-shared-list-dialog>
<hd-chart-birthdays-dialog [(open)]="birthdaysDialogOpen" (chartOpenRequested)="loadChart($event)"></hd-chart-birthdays-dialog>
@if (mode() === 'dialog') {
  <hd-rave-new-year-dialog [(open)]="raveNewYearDialogOpen" (requested)="raveNewYearRequested.emit($event)"></hd-rave-new-year-dialog>
}
<!-- Import uit Maia Mechanics (Online-CSV / Desktop-.mmi); de run zelf leeft in een
     root-service en loopt door als deze dialog of de manager gesloten wordt. -->
<e-dialog size="lg" [title]="text().importTitle" [(open)]="importDialogOpen">
  <ng-template>
    <hd-chart-import-panel />
  </ng-template>
</e-dialog>
<!-- Export naar Mechanics in Motion (.json, terug te importeren) of naar Maia Mechanics /
     MyBodyGraph; enkel standaardcharts, niets persoonlijks. -->
<e-export-dialog
  [open]="exportScope() !== undefined"
  (openChange)="onExportOpenChange($event)"
  [title]="exportTitle()"
  [intro]="exportIntro()"
  [options]="exportOptions()"
  [labels]="exportLabels()"
  [busy]="exporting()"
  (exportRequested)="runExport($event)"
/>
